import { Check } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { AdjustableImage } from "@/components/ImageAdjust";

const bulletsOf = (text) => (text || "").split("\n").map((l) => l.trim().replace(/^[-•*]\s+/, "")).filter(Boolean);
const SectionTitle = ({ children, light, font }) => (
  <h2 className={`text-2xl sm:text-3xl font-bold tracking-tight mb-8 ${light ? "text-slate-900" : "text-white"}`} style={{ fontFamily: `"${font}", sans-serif` }}>{children}</h2>
);

export const HeroBanner = ({ path, st }) =>
  path ? <AdjustableImage testid="landing-hero" src={assetUrl(path)} st={st} aspect="aspect-[21/7]" className="mb-10 rounded-2xl bg-black/20" /> : null;

export const Takeaways = ({ data, light, accent, headingFont, st }) => {
  const items = bulletsOf(data?.text);
  if (!items.length) return null;
  return (
    <section data-testid="landing-takeaways" className={`mt-16 grid ${data.image_path ? "lg:grid-cols-2" : ""} gap-12 items-center`}>
      <div>
        <SectionTitle light={light} font={headingFont}>{data.heading || "Key takeaways"}</SectionTitle>
        <ul className="space-y-4">
          {items.map((x, i) => (
            <li key={i} className={`flex items-start gap-3 ${light ? "text-slate-700" : "text-slate-300"}`}>
              <span className="mt-0.5 w-6 h-6 rounded-full grid place-items-center shrink-0" style={{ background: `${accent}26`, color: accent }}><Check className="w-3.5 h-3.5" /></span>
              {x}
            </li>
          ))}
        </ul>
      </div>
      {data.image_path && <AdjustableImage testid="landing-takeaways-image" src={assetUrl(data.image_path)} st={st} aspect="aspect-[4/3]" className="w-full rounded-2xl" />}
    </section>
  );
};

export const Gallery = ({ data, light, headingFont }) => {
  if (!data?.images?.length) return null;
  return (
    <section data-testid="landing-gallery" className="mt-16">
      {data.heading && <SectionTitle light={light} font={headingFont}>{data.heading}</SectionTitle>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {data.images.map((p, i) => (
          <div key={p} className={`overflow-hidden rounded-xl ${i === 0 && data.images.length > 2 ? "md:col-span-2 md:row-span-2" : ""}`}>
            <img src={assetUrl(p)} alt="" className="w-full h-full object-cover aspect-[4/3] hover:scale-105 transition-transform duration-500" />
          </div>
        ))}
      </div>
    </section>
  );
};

export const Partners = ({ data, light }) => {
  if (!data?.images?.length) return null;
  return (
    <section data-testid="landing-partners" className={`mt-16 pt-10 border-t ${light ? "border-slate-200" : "border-white/10"}`}>
      {data.heading && <p className={`text-center font-mono text-[11px] uppercase tracking-[0.25em] mb-8 ${light ? "text-slate-500" : "text-slate-400"}`}>{data.heading}</p>}
      <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
        {data.images.map((p) => (
          <img key={p} src={assetUrl(p)} alt="Partner logo" className={`h-9 w-auto max-w-[150px] object-contain opacity-70 hover:opacity-100 transition-opacity ${light ? "" : "brightness-0 invert"}`} />
        ))}
      </div>
    </section>
  );
};
